import React, {Component} from 'react';
import PropTypes from "prop-types";
import {Button, Dropdown, DropdownItem, DropdownMenu, DropdownToggle} from "reactstrap";
import DayPicker, { DateUtils } from 'react-day-picker';
import moment from 'moment';
import {IconToTheSide} from "..";
import {isEqual} from 'lodash';

/**
 * Note some weird behavior here:
 * Named options will call onSelected with themselves, (so if you're using Date or moment,
 * you'll get a Date or moment back)
 * _however_ the custom date range will fire _timestamp integers_
 *
 * If you want consistent types, use timestamp integers for the from/to on the named options passed in
 *
 */
export class DateRangeSelector extends Component {
  static propTypes = {
    selected: PropTypes.shape({
      from: PropTypes.any, to: PropTypes.any
    }),
    namedOptions: PropTypes.arrayOf(PropTypes.shape({
      from: PropTypes.any, to: PropTypes.any, // date (something that moment can be built from)
      name: PropTypes.string
    })),
    format: PropTypes.string,
    hideSelectedRange: PropTypes.bool,
    onSelected: PropTypes.func,                // ({ from: moment, to: moment}) -> void
    disabled: PropTypes.bool,                  // if true, disables the dropdown
    required: PropTypes.bool,
    disabledDays: PropTypes.any,
    right: PropTypes.bool // aligns the menu right
  };

  static defaultProps = {
    placeholder: <span className="text-muted">Select...</span>,
    disabled: false,
    namedOptions: [],
    format: 'M/D/YY',
    onSelected: (x) => {},
  };

  state = {
    isOpen: false,
    selectedRange: null
  }

  componentWillReceiveProps(nextProps, nextState) {
    const nextSelected = nextProps.selected ? { from: nextProps.selected.from, to: nextProps.selected.to } : null
    const currentSelected = this.props.selected ? { from: this.props.selected.from, to: this.props.selected.to } : null

    if (this.state.selectedRange && !areDateRangesEqual(currentSelected, nextSelected)) {
      this.setState({ selectedRange: null })
    }

  }

  handleDayClick = (day) => {
    const selectedRange = {...this.state.selectedRange}
    selectedRange.from = selectedRange.from || null
    selectedRange.to = selectedRange.to || null
    const newRange = DateUtils.addDayToRange(day, selectedRange);
    this.setState({ selectedRange: newRange})
  }

  toggle = () => {
    let isOpen = !this.state.isOpen;
    this.setState({ isOpen });
  };

  addSelection(item) {
    this.selected = item;
    this.props.onSelected(item);
    this.setState({ selectedRange: null });
  }

  // in case the range set from the props is not a named option, get the selected range on the fly
  selectedRangeOrComputed() {
    if (this.state.selectedRange) {
      return toDateRange(this.state.selectedRange)
    } else {
      return toDateRange(this.props.selected)
    }
  }



  removeSelection = e => {
    if (!this.props.disabled) {
      e.stopPropagation()
      this.props.onSelected(undefined)
    }
  };
  renderActive(active) {
    const { from, to } = this.selectedRangeOrComputed()
    const format = this.props.format;
    let selectedDisplay;
    if (!active) {
      selectedDisplay = this.props.placeholder;
    } else if (active === SELECTED_RANGE) {
      const toDisplay = to ? moment(to).format(format) : '...';
      const fromDisplay = from ? moment(from).format(format) : '...';
      selectedDisplay = fromDisplay === toDisplay ? fromDisplay : `${fromDisplay} - ${toDisplay}`;
    } else {
      selectedDisplay =
        active.name
          ? active.name
          : (`${moment(active.from).format(format)} - ${moment(active.to).format(format)}`)

    }

    const icon = (!this.props.required && !!active)
      ? <span onClick={this.removeSelection} className="far fa-times"/>
      : <span className="far fa-chevron-down" data-testid="date-range-chevron-down"/>;
    return <IconToTheSide side="right" icon={icon}>{selectedDisplay}</IconToTheSide>

  }


  onCancelDateRange = () => {
    this.setState({ selectedRange: null, isOpen: false });
  }

  onApplyDateRange = () => {
    this.setState({ isOpen: false });
    const {from, to} = this.state.selectedRange
    this.props.onSelected({from: from && from.getTime(), to: to && to.getTime()});
  }

  onSelectRange = () => {
    this.setState({ selectedRange: {from: null, to: null}});
  }

  calculateSelected() {
    if (this.state.selectedRange) {
      return SELECTED_RANGE
    } else if (!this.props.selected) {
      return null
    } else {
      let selection;
      const selectedRange = toDateRange(this.props.selected)
      selection = this.props.namedOptions.find(x => x === this.props.selected)
      selection = selection || this.props.namedOptions.find(x => areDateRangesEqual(toDateRange(x), selectedRange))
      selection = selection || SELECTED_RANGE
      return selection
    }
  }

  renderRangeSelector() {
    const {from, to} = this.selectedRangeOrComputed()
    const modifiers = { start: from, end: to}
    return <div className="col-md-9">
      <DayPicker
        selectedDays={[from, {from, to}]}
        modifiers={modifiers}
        numberOfMonths={2}
        onDayClick={this.handleDayClick}
        disabledDays={this.props.disabledDays}
      />
      <div className="text-right col-md-12">
        <Button outline={true} color="secondary" onClick={this.onCancelDateRange}>Cancel</Button>
        <Button className="control-spacer-left" color="secondary" onClick={this.onApplyDateRange}>Apply</Button>
      </div>
    </div>
  }

  render() {
    const { disabled, hideSelectedRange, right } = this.props;
    const { isOpen } = this.state;

    const active = this.calculateSelected()

    const menuItems =
      this.props.namedOptions.map(x => {
        return <DropdownItem
          key={x.name}
          active={x === active}
          onClick={() => this.addSelection(x)}
        >{x.name}</DropdownItem>
      }).concat(
        hideSelectedRange
          ? []
          : [<DropdownItem
            toggle={false}
            active={active === SELECTED_RANGE}
            key="sel-date-range"
            onClick={this.onSelectRange}
            >Select Range</DropdownItem>]
      );

    return (
      <Dropdown
        isOpen={isOpen}
        toggle={this.toggle}
        data-testid={this.props.dataTestId}
      >
        <DropdownToggle disabled={disabled} tag={'span'} className="d-block form-control dropdown-toggle dropdown-toggle-no-icon">
          {this.renderActive(active)}
        </DropdownToggle>
        <DropdownMenu
          style={{maxWidth: "90vw", overflowX: "unset"}}
          positionFixed={true}
          data-testid="date-range-options"
        >
          {
            (active !== SELECTED_RANGE)
              ? menuItems
              : (
                <div className="date-dropdown row" style={{width: '668px', maxWidth: '100vw'}}>
                  <div className="col-md-3">
                    {menuItems}
                  </div>
                  {this.renderRangeSelector()}
                </div>
              )
          }
        </DropdownMenu>
      </Dropdown>
    );
  }
}

const SELECTED_RANGE = 'SELECTED_RANGE';

function toDateRange(obj) {
  if (!obj) {
    return { from: null, to: null }
  } else {
    return {
      from: obj.from ? moment(obj.from).endOf('day').utcOffset(0).toDate() : null,
      to:   obj.to ? moment(obj.to).endOf('day').utcOffset(0).toDate() : null
    }
  }
}

function valueOfDateRange({ from, to }) {
  return { from: from ? from.valueOf() : null, to: to ? to.valueOf() : null }
}
function areDateRangesEqual(a, b) {
  if (!a && !b) {
    return true
  } else if (!a || !b) {
    return false
  } else {
    return isEqual(valueOfDateRange(a), valueOfDateRange(b))
  }
}
